import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useFlowSocket } from '../hooks/useFlowSocket';
import { useTableSort, sortArrow } from '../hooks/useTableSort';
import IntakeQRCode from '../components/IntakeQRCode';
import '../styles/PatientFlow.css';

const STAGES = [
  { key: 'scheduled', label: 'Scheduled', icon: '📅', color: '#6366f1' },
  { key: 'intake_sent', label: 'Intake Sent', icon: '📤', color: '#8b5cf6' },
  { key: 'form_opened', label: 'Form Opened', icon: '📋', color: '#a855f7' },
  { key: 'filling_out', label: 'Filling Out', icon: '✍️', color: '#f59e0b' },
  { key: 'completed', label: 'Intake Done', icon: '✅', color: '#10b981' },
  { key: 'checked_in', label: 'Checked In', icon: '🏥', color: '#06b6d4' },
  { key: 'in_exam', label: 'In Exam', icon: '🩺', color: '#3b82f6' },
  { key: 'done', label: 'Visit Done', icon: '🎉', color: '#22c55e' },
];

const TERMINAL_STAGES = [
  { key: 'no_show', label: 'No Show', icon: '❌', color: '#ef4444' },
  { key: 'canceled', label: 'Canceled', icon: '🚫', color: '#9ca3af' },
];

function PatientFlow({ practiceId }) {
  const [snapshot, setSnapshot] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stagePatients, setStagePatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('today');

  // WebSocket for real-time updates
  const { snapshot: wsSnapshot, lastEvent, connected: wsConnected } = useFlowSocket(practiceId);

  // Apply WebSocket snapshot when received
  useEffect(() => {
    if (wsSnapshot && dateRange === 'today') {
      setSnapshot(wsSnapshot);
    }
  }, [wsSnapshot, dateRange]);

  // Re-fetch stage detail when a flow event affects the selected stage
  useEffect(() => {
    if (lastEvent && selectedStage) {
      if (lastEvent.current_stage === selectedStage || lastEvent.previous_stage === selectedStage) {
        loadStageDetail(selectedStage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  const getDateParams = useCallback(() => {
    const now = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const todayStr = fmt(now);
    // Patient Flow tracks who's moving through visits — practice admins
    // care about today + upcoming. The week/month options were historically
    // lookback windows, which surfaced already-completed visits with no
    // actionable state. Flipping to forward-looking.
    if (dateRange === 'today') {
      return { date_from: todayStr, date_to: todayStr };
    } else if (dateRange === 'week') {
      const weekAhead = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
      return { date_from: todayStr, date_to: fmt(weekAhead) };
    }
    const monthAhead = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30);
    return { date_from: todayStr, date_to: fmt(monthAhead) };
  }, [dateRange]);

  const loadSnapshot = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const params = getDateParams();
      const result = await api.getFlowSnapshot(practiceId, params);
      if (result.success) {
        setSnapshot(result.data);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId, getDateParams]);

  useEffect(() => {
    loadSnapshot();
    const interval = setInterval(loadSnapshot, 15000);
    return () => clearInterval(interval);
  }, [loadSnapshot]);

  const loadStageDetail = useCallback(async (stage) => {
    if (!practiceId) return;
    try {
      setDetailLoading(true);
      const params = getDateParams();
      const result = await api.getFlowStageDetail(practiceId, stage, params);
      if (result.success) {
        setStagePatients(result.data || []);
      }
    } catch (err) {
      setStagePatients([]);
    } finally {
      setDetailLoading(false);
    }
  }, [practiceId, getDateParams]);

  const handleStageClick = (stageKey) => {
    if (selectedStage === stageKey) {
      setSelectedStage(null);
      setStagePatients([]);
    } else {
      setSelectedStage(stageKey);
      loadStageDetail(stageKey);
    }
  };

  if (!practiceId) {
    return <div className="pf-container"><p>Select a practice to view patient flow.</p></div>;
  }

  const stages = snapshot?.stages || {};
  const kpis = snapshot?.kpis || {};
  const totalActive = STAGES.reduce((sum, s) => sum + (stages[s.key] || 0), 0);

  return (
    <div className="pf-container">
      <div className="pf-header">
        <div>
          <h1 className="pf-title">Patient Flow</h1>
          <p className="pf-subtitle">Real-time view of patients moving through their visit journey</p>
        </div>
        <div className="pf-controls">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="pf-select" title="Forward-looking — visits scheduled in this window">
            <option value="today">Today</option>
            <option value="week">Next 7 Days</option>
            <option value="month">Next 30 Days</option>
          </select>
          <div className={`pf-live-indicator ${wsConnected ? 'live' : loading ? 'loading' : 'live'}`}>
            <span className="pf-live-dot" />
            {wsConnected ? 'Live' : loading ? 'Updating...' : 'Polling'}
          </div>
        </div>
      </div>

      {error && <div className="pf-error">Failed to load: {error}</div>}

      {/* KPI Cards */}
      <div className="pf-kpi-row">
        <KpiCard label="Total Appointments" value={kpis.total_appointments} color="#6366f1" />
        <KpiCard label="Intake Completed" value={kpis.intake_completed} color="#10b981" />
        <KpiCard label="Intake Pending" value={kpis.intake_pending} color="#f59e0b" />
        <KpiCard label="Visits Done" value={kpis.visits_done} color="#22c55e" />
        <KpiCard label="No Shows" value={kpis.no_shows} color="#ef4444" />
        {kpis.bottleneck_stage && (
          <KpiCard label="Bottleneck" value={kpis.bottleneck_stage.replace(/_/g, ' ')} color="#f97316" isText />
        )}
      </div>

      {/* Stage Pipeline */}
      <div className="pf-pipeline-section">
        <h2 className="pf-section-title">Patient Pipeline</h2>
        <div className="pf-pipeline">
          {STAGES.map((stage, i) => {
            const count = stages[stage.key] || 0;
            const pct = totalActive > 0 ? (count / totalActive * 100) : 0;
            const isSelected = selectedStage === stage.key;
            return (
              <div key={stage.key} className="pf-stage-wrapper">
                <div
                  className={`pf-stage ${isSelected ? 'selected' : ''} ${count > 0 ? 'has-patients' : ''}`}
                  style={{ '--stage-color': stage.color }}
                  onClick={() => handleStageClick(stage.key)}
                >
                  <div className="pf-stage-icon">{stage.icon}</div>
                  <div className={`pf-stage-count ${count > 0 ? 'pulse' : ''}`}>{count}</div>
                  <div className="pf-stage-label">{stage.label}</div>
                  {pct > 0 && <div className="pf-stage-pct">{Math.round(pct)}%</div>}
                </div>
                {i < STAGES.length - 1 && (
                  <div className="pf-stage-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14m-4-4l4 4-4 4" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Terminal stages row */}
        <div className="pf-terminal-row">
          {TERMINAL_STAGES.map((stage) => {
            const count = stages[stage.key] || 0;
            const isSelected = selectedStage === stage.key;
            return (
              <div
                key={stage.key}
                className={`pf-terminal-stage ${isSelected ? 'selected' : ''}`}
                style={{ '--stage-color': stage.color }}
                onClick={() => handleStageClick(stage.key)}
              >
                <span className="pf-stage-icon">{stage.icon}</span>
                <span className="pf-terminal-count">{count}</span>
                <span className="pf-terminal-label">{stage.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Detail Panel */}
      {selectedStage && (
        <div className="pf-detail-panel">
          <div className="pf-detail-header">
            <h3>
              {[...STAGES, ...TERMINAL_STAGES].find(s => s.key === selectedStage)?.icon}{' '}
              {[...STAGES, ...TERMINAL_STAGES].find(s => s.key === selectedStage)?.label}{' '}
              ({stagePatients.length} patients)
            </h3>
            <button className="pf-detail-close" onClick={() => { setSelectedStage(null); setStagePatients([]); }}>
              Close
            </button>
          </div>
          {detailLoading ? (
            <div className="pf-detail-loading">Loading patients...</div>
          ) : stagePatients.length === 0 ? (
            <div className="pf-detail-empty">No patients in this stage</div>
          ) : (
            <StagePatientTable patients={stagePatients} />
          )}
        </div>
      )}
    </div>
  );
}

function StagePatientTable({ patients }) {
  const sortRows = patients.map(p => ({
    ...p,
    _patient_name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
    _completion_pct_num: p.completion_pct == null ? null : Number(p.completion_pct),
    _appt_ms: p.appointment_date ? new Date(p.appointment_date).getTime() : null,
  }));
  const { sortedRows, sortKey, sortDir, handleSort } = useTableSort(sortRows);
  const thStyle = { cursor: 'pointer', userSelect: 'none' };
  return (
    <table className="pf-detail-table">
      <thead>
        <tr>
          <th style={thStyle} onClick={() => handleSort('_patient_name')}>Patient{sortArrow('_patient_name', sortKey, sortDir)}</th>
          <th style={thStyle} onClick={() => handleSort('_appt_ms')}>Appointment{sortArrow('_appt_ms', sortKey, sortDir)}</th>
          <th style={thStyle} onClick={() => handleSort('appointment_type')}>Type{sortArrow('appointment_type', sortKey, sortDir)}</th>
          <th style={thStyle} onClick={() => handleSort('intake_status')}>Intake Status{sortArrow('intake_status', sortKey, sortDir)}</th>
          <th style={thStyle} onClick={() => handleSort('_completion_pct_num')}>Completion{sortArrow('_completion_pct_num', sortKey, sortDir)}</th>
          <th>QR Code</th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((p) => (
                  <tr key={p.appointment_id}>
                    <td className="pf-patient-name">{p.first_name} {p.last_name}</td>
                    <td>{p.appointment_date ? (() => { const pts = String(p.appointment_date).split('T')[0].split('-').map(Number); return pts.length === 3 ? new Date(Date.UTC(pts[0], pts[1]-1, pts[2], 12)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : p.appointment_date; })() : '—'}</td>
                    <td>{p.appointment_type || '—'}</td>
                    <td>
                      <span className={`pf-status-badge pf-status-${(p.intake_status || 'none').toLowerCase()}`}>
                        {p.intake_status || 'No Form'}
                      </span>
                    </td>
                    <td>
                      {p.completion_pct != null ? (
                        <div className="pf-progress-bar-wrapper">
                          <div className="pf-progress-bar" style={{ width: `${p.completion_pct}%` }} />
                          <span>{p.completion_pct}%</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      {p.intake_access_token && p.intake_status !== 'COMPLETED' ? (
                        <IntakeQRCode accessToken={p.intake_access_token} size={80} />
                      ) : p.intake_status === 'COMPLETED' ? '✅' : '—'}
                    </td>
                  </tr>
        ))}
      </tbody>
    </table>
  );
}

function KpiCard({ label, value, color, isText }) {
  return (
    <div className="pf-kpi-card" style={{ borderTopColor: color }}>
      <div className="pf-kpi-value" style={{ color: isText ? color : undefined }}>
        {isText ? value : (value ?? 0)}
      </div>
      <div className="pf-kpi-label">{label}</div>
    </div>
  );
}

export default PatientFlow;
